import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Send, UserMinus, Save } from "lucide-react";
import { api } from "@/lib/api";
import { formatPhone } from "@/lib/applyApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fmtMT } from "@/lib/mountainTime";

const INVITE = { sent: ["Invitation emailed", "text-emerald-700"], sandbox_skipped: ["Preview sandbox — not emailed", "text-amber-700"], failed: ["Invitation email failed", "text-red-700"] };
const err = (e) => e?.response?.data?.detail || "Something went wrong";
const notice = (r, ok) => (r?.email_status && r.email_status !== "sent" ? toast.warning : toast.success)(`${ok}${r?.email_status ? ` · ${INVITE[r.email_status]?.[0] || ""}` : ""}`);

function AccessRow({ t, appId, reload, readOnly }) {
  const [busy, setBusy] = useState(false);
  const run = async (fn, ok) => {
    setBusy(true);
    try { notice(await fn(), ok); reload(); } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  const [inv, tone] = INVITE[t.invite_status] || ["No invitation sent", "text-stone-500"];
  const active = t.status === "active";
  return (
    <li data-testid={`admin-trainer-access-${t.id}`} className="py-2 flex flex-col sm:flex-row sm:items-center gap-2 text-sm">
      <div className="min-w-0 w-full sm:flex-1">
        <p className="font-semibold text-stone-900 break-all">{t.name ? `${t.name} · ` : ""}{t.email}</p>
        <p data-testid={`admin-trainer-access-status-${t.id}`} className="text-xs text-stone-600">
          {active ? <b className={t.signed_in ? "text-emerald-700" : "text-amber-700"}>{t.signed_in ? "Active — signed in" : "Invited — waiting for first sign-in"}</b> : `Access removed ${t.revoked_at ? fmtMT(t.revoked_at) : ""}`}
          {active && <> · <span className={tone}>{inv}{t.invite_at ? ` ${fmtMT(t.invite_at)}` : ""}</span></>}
        </p>
      </div>
      {active && !readOnly && (
        <div className="flex flex-wrap gap-2 shrink-0">
          <Button data-testid={`admin-trainer-resend-${t.id}`} size="sm" variant="outline" disabled={busy} className="rounded-full h-8"
            onClick={() => run(() => api.post(`/applications/${appId}/trainers/${t.id}/resend`).then((r) => r.data), "Invitation re-sent")}><Send className="h-3.5 w-3.5 mr-1" />Resend</Button>
          <Button data-testid={`admin-trainer-revoke-${t.id}`} size="sm" variant="outline" disabled={busy} className="rounded-full h-8 text-red-700"
            onClick={() => window.confirm(`Remove ${t.name || t.email}'s access?`) && run(() => api.delete(`/applications/${appId}/trainers/${t.id}`).then(() => null), "Trainer access removed")}><UserMinus className="h-3.5 w-3.5 mr-1" />Remove access</Button>
        </div>
      )}
    </li>
  );
}

function InviteForm({ a, h, reload, onDone, first }) {
  const [f, setF] = useState({ name: first ? h.trainer_name || "" : "", phone: first ? h.trainer_phone || "" : "", email: first ? h.trainer_email || "" : "" });
  const [allow, setAllow] = useState("yes");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: k === "phone" ? formatPhone(e.target.value) : e.target.value });
  const save = async () => {
    setBusy(true);
    try {
      const r = allow === "yes" ? (await api.post(`/applications/${a.id}/trainers`, f)).data : (await api.put(`/applications/${a.id}/trainer-email`, { email: f.email })).data;
      notice(r, allow === "yes" ? "Trainer saved and invited" : "Trainer email saved (no access given)");
      onDone?.(); reload();
    } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="admin-trainer-form" className="space-y-2">
      <div className="grid sm:grid-cols-3 gap-2">
        <Input data-testid="admin-trainer-name-input" placeholder="Trainer name" value={f.name} onChange={set("name")} className="h-10" />
        <Input data-testid="admin-trainer-phone-input" placeholder="Phone (optional)" inputMode="tel" value={f.phone} onChange={set("phone")} className="h-10" />
        <Input id="admin-trainer-email" data-testid="admin-trainer-email-input" type="email" placeholder="trainer@example.com" value={f.email} onChange={set("email")} className="h-10" />
      </div>
      <div data-testid="admin-trainer-allow" className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-xs font-semibold text-stone-600 mr-1">Allow Trainer Access</span>
        {[["yes", "Yes"], ["no", "No"]].map(([v, l]) => (
          <button key={v} type="button" data-testid={`admin-trainer-allow-${v}`} onClick={() => setAllow(v)} aria-pressed={allow === v}
            className={`h-8 px-4 rounded-full border text-xs font-semibold transition-colors ${allow === v ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-300 hover:bg-stone-50"}`}>{l}</button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button data-testid="admin-trainer-save" disabled={busy || !f.email.trim() || (allow === "yes" && !f.name.trim())} className="rounded-full h-9 bg-amber-800 hover:bg-amber-900 text-white" onClick={save}>
          {allow === "yes" ? <><UserPlus className="h-4 w-4 mr-1" />Save &amp; Send Invitation</> : <><Save className="h-4 w-4 mr-1" />Save email (no access)</>}
        </Button>
        {onDone && <Button data-testid="admin-trainer-cancel" variant="ghost" className="rounded-full h-9" onClick={onDone}>Cancel</Button>}
      </div>
      <p className="text-xs text-stone-500">The trainer can manage the Ad Template, photos and videos. They can't open or sign the contract or see accounting. The invitation signs them in with one click — no account needed.</p>
    </div>
  );
}

// Admin Trainer Access: reuses the existing trainer fields, access grants and invitation email.
export function AdminTrainerCard({ a, reload, readOnly }) {
  const h = a.horse || {};
  const [adding, setAdding] = useState(false);
  useEffect(() => { setAdding(false); }, [a.id]);
  const list = a.trainer_access || [];
  const active = list.filter((t) => t.status === "active");
  return (
    <section data-testid="admin-trainer-card" className="bg-white border border-stone-200 rounded-xl p-4 sm:p-5">
      <h2 className="font-semibold text-stone-900 mb-3">Trainer Access</h2>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm mb-3">
        <dt className="text-stone-500">Name</dt><dd data-testid="admin-trainer-name" className="font-medium text-stone-900">{h.trainer_name || "—"}</dd>
        <dt className="text-stone-500">Phone</dt><dd data-testid="admin-trainer-phone" className="font-medium text-stone-900">{h.trainer_phone ? formatPhone(h.trainer_phone) : "—"}</dd>
        <dt className="text-stone-500">Email</dt><dd data-testid="admin-trainer-email" className="font-medium text-stone-900 break-all">{h.trainer_email || "—"}</dd>
      </dl>
      {list.length > 0 && <ul data-testid="admin-trainer-access-list" className="mb-3 divide-y divide-stone-100 border-y border-stone-100">{list.map((t) => <AccessRow key={t.id} t={t} appId={a.id} reload={reload} readOnly={readOnly} />)}</ul>}
      {h.trainer_is_seller ? <p className="text-xs text-stone-500">The owner is this horse's trainer.</p> : !readOnly && (
        active.length && !adding
          ? <Button data-testid="admin-trainer-add-another" size="sm" variant="outline" className="rounded-full" onClick={() => setAdding(true)}><UserPlus className="h-4 w-4 mr-1" />Add another trainer</Button>
          : <InviteForm a={a} h={h} reload={reload} first={!active.length} onDone={active.length ? () => setAdding(false) : undefined} />
      )}
    </section>
  );
}
